import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Layout from "../../../component/layout/Layout";
import MomentHeaderCard from "../components/MomentHeaderCard";
import MomentMessageComposer from "../components/MomentMessageComposer";
import MomentPhonePreview from "../components/MomentPhonePreview";
import MomentTestSendModal from "../components/MomentTestSendModal";
import { MOMENT_POLICY_ITEMS } from "../data/momentOptions";
import {
  getMyAccountStatus,
  getCurrentUserInfo,
  getMessageAdDetail,
  saveMessageAd,
  updateMessageAd,
  sendTestMessage,
  type MomentAccountStatus,
  type CurrentUserInfo,
  type MomentAdDetailDto,
} from "../api/momentApi";
import { useMomentForm } from "../hooks/useMomentForm";
import { buildMessageBody } from "../utils/messageBodyBuilder";
import { validateBeforeSave } from "../utils/messageValidator";
import { uploadPendingImages } from "../utils/imageUploader";
import { canEditReadyMessage } from "../utils/dateTimeUtils";
import { formatPhoneNumber, isValidMobilePhoneNumber, normalizePhoneNumber } from "../utils/phoneUtils";

const TEST_SEND_COOLDOWN_MS = 30_000;

export default function MomentCreatePage() {
  return (
    <Layout>
      <MomentCreateGuard />
    </Layout>
  );
}

function MomentCreateGuard() {
  const navigate = useNavigate();
  const [account, setAccount] = useState<MomentAccountStatus | null | undefined>(undefined);

  useEffect(() => {
    getMyAccountStatus()
      .then((data) => setAccount(data ?? null))
      .catch(() => setAccount(null));
  }, []);

  if (account === undefined) {
    return (
      <div className="moment-page tw-min-h-[calc(100vh-200px)] tw-flex tw-items-center tw-justify-center tw-text-gray-400 tw-text-sm">
        불러오는 중...
      </div>
    );
  }

  const isReady = account !== null && account.status === "ACTIVE" && !!account.kakaoAdAccountId;

  if (!isReady) {
    return (
      <div className="moment-page tw-min-h-[calc(100vh-200px)] tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-4">
        <div className="tw-text-center tw-space-y-2">
          <p className="tw-text-lg tw-font-semibold tw-text-gray-700">채널 연동이 완료되지 않았습니다.</p>
          <p className="tw-text-sm tw-text-gray-500">
            메시지 광고를 등록하려면 광고계정과 카카오톡 채널 연동이 완료되어야 합니다.
          </p>
        </div>
        <button
          onClick={() => navigate("/moment/description")}
          className="tw-px-5 tw-py-2.5 tw-bg-[#5D87FF] tw-text-white tw-rounded-xl tw-text-sm tw-font-bold"
        >
          서비스 신청 페이지로 이동
        </button>
      </div>
    );
  }

  const channelLabel = account.channelName ?? account.channelSearchId ?? "";
  return <MomentCreateContent channelLabel={channelLabel} kakaoAdAccountId={account.kakaoAdAccountId!} />;
}


function MomentCreateContent({ channelLabel, kakaoAdAccountId }: { channelLabel: string; kakaoAdAccountId: number }) {
  void kakaoAdAccountId;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const form = useMomentForm(channelLabel);

  // ── UI 상태 ──
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showTestModal, setShowTestModal] = useState(false);
  const [testPhone, setTestPhone] = useState("");
  const [statusMessage, setStatusMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);
  const [showToast, setShowToast] = useState(false);
  const [readonlyDetail, setReadonlyDetail] = useState<MomentAdDetailDto | null>(null);
  const [currentUser, setCurrentUser] = useState<CurrentUserInfo | null>(null);
  const [testCooldownSeconds, setTestCooldownSeconds] = useState(0);
  const draftAdId = searchParams.get("draftAdId");
  const readonly = searchParams.get("readonly") === "true";
  const currentUserPhone = currentUser?.phone ?? "";

  const getCooldownStorageKey = () =>
    `moment:test-send:cooldown:create:${form.savedAd?.kakaoAdId ?? draftAdId ?? "new"}`;

  const syncCooldownFromStorage = () => {
    const raw = window.sessionStorage.getItem(getCooldownStorageKey());
    if (!raw) {
      setTestCooldownSeconds(0);
      return;
    }

    const expiresAt = Number(raw);
    if (!Number.isFinite(expiresAt)) {
      window.sessionStorage.removeItem(getCooldownStorageKey());
      setTestCooldownSeconds(0);
      return;
    }

    const remainingMs = expiresAt - Date.now();
    if (remainingMs <= 0) {
      window.sessionStorage.removeItem(getCooldownStorageKey());
      setTestCooldownSeconds(0);
      return;
    }

    setTestCooldownSeconds(Math.ceil(remainingMs / 1000));
  };

  useEffect(() => {
    if (!statusMessage) { setShowToast(false); return; }
    setShowToast(true);
    const hideTimer = window.setTimeout(() => setShowToast(false), 2400);
    const clearTimer = window.setTimeout(() => setStatusMessage(null), 2800);
    return () => { window.clearTimeout(hideTimer); window.clearTimeout(clearTimer); };
  }, [statusMessage]);

  useEffect(() => {
    if (!showTestModal) return;
    setTestPhone(formatPhoneNumber(currentUserPhone));
  }, [currentUserPhone, showTestModal]);

  useEffect(() => {
    getCurrentUserInfo()
      .then((data) => setCurrentUser(data))
      .catch(() => setCurrentUser(null));
  }, []);

  useEffect(() => {
    syncCooldownFromStorage();
    const timer = window.setInterval(syncCooldownFromStorage, 1000);
    return () => window.clearInterval(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.savedAd?.kakaoAdId, draftAdId]);

  // ── 임시저장 불러오기 (편집) ──
  useEffect(() => {
    if (!draftAdId || readonly) return;
    setIsLoading(true);
    setStatusMessage(null);
    getMessageAdDetail(draftAdId)
      .then((data) => {
        if (!["DRAFT", "READY"].includes(data.status)) {
          throw new Error("수정 가능한 메시지 광고가 아닙니다.");
        }
        if (data.status === "READY" && !canEditReadyMessage(data.sendDate)) {
          throw new Error("메시지 내용 수정은 발송 10분 전까지만 가능합니다.");
        }
        form.hydrateFromDetail(data);
        setStatusMessage({
          type: "success",
          text: data.status === "READY" ? "발송대기 메시지를 불러왔습니다." : "임시저장 메시지를 불러왔습니다.",
        });
      })
      .catch((e: unknown) => {
        const msg = e instanceof Error ? e.message : "임시저장 메시지 불러오기 중 오류가 발생했습니다.";
        setStatusMessage({ type: "error", text: msg });
        navigate("/moment/list");
      })
      .finally(() => setIsLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftAdId, readonly, navigate]);

  // ── 읽기 전용 불러오기 (발송 취소 내용 확인) ──
  useEffect(() => {
    if (!draftAdId || !readonly) return;
    setIsLoading(true);
    getMessageAdDetail(draftAdId)
      .then((data) => {
        setReadonlyDetail(data);
        form.hydrateFromDetail(data);
      })
      .catch((e: unknown) => {
        const msg = e instanceof Error ? e.message : "광고 정보를 불러오는 중 오류가 발생했습니다.";
        setStatusMessage({ type: "error", text: msg });
      })
      .finally(() => setIsLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftAdId]);

  // ── 저장 ──
  const handleSave = async () => {
    if (form.savedAd?.status === "READY" && !canEditReadyMessage(form.savedAd.sendDate)) {
      alert("메시지 내용 수정은 발송 10분 전까지만 가능합니다.");
      navigate("/moment/list");
      return;
    }

    if (form.isLoadedDraft && form.originalMessageType && form.messageType !== form.originalMessageType) {
      alert("불러온 임시저장 메시지는 기존과 동일한 메시지 유형만 선택할 수 있습니다. 메시지 유형 변경이 필요하면 새로운 메시지를 생성해주세요.");
      return;
    }

    const validationMessage = validateBeforeSave({
      messageName: form.messageName,
      messageType: form.messageType,
      messageBody: form.messageBody,
      messageTitle: form.messageTitle,
      wideImage: form.wideImage,
      landingMobileUrl: form.landingMobileUrl,
      buttons: form.buttons,
      couponEnabled: form.couponEnabled,
      coupon: form.coupon,
      moreButtonEnabled: form.moreButtonEnabled,
      moreButton: form.moreButton,
      listItems: form.listItems,
      carouselItems: form.carouselItems,
      commerceIntroEnabled: form.commerceIntroEnabled,
      commerceIntro: form.commerceIntro,
    });
    if (validationMessage) { alert(validationMessage); return; }

    setIsLoading(true);
    setIsSaving(true);
    setStatusMessage(null);
    try {
      const timestamp = new Date().toISOString().replace(/[-:TZ.]/g, "").slice(0, 14);
      const uploadedImages = await uploadPendingImages(
        form.messageType, form.wideImage, form.listItems, form.carouselItems, form.commerceIntro, form.commerceIntroEnabled,
        timestamp, form.setWideImage, form.setListItems, form.setCarouselItems, form.setCommerceIntro
      );
      const payload = {
        adName: form.messageName.trim(),
        messageType: form.messageType,
        ageVerification: form.ageAuthEnabled,
        message: buildMessageBody({
          messageType: form.messageType,
          adFlag: form.adFlag,
          messageBody: form.messageBody,
          messageTitle: form.messageTitle,
          shareFlag: form.shareFlag,
          buttons: form.buttonsEnabled ? form.buttons : [],
          couponEnabled: form.couponEnabled,
          coupon: form.coupon,
          moreButtonEnabled: form.moreButtonEnabled,
          commerceIntroEnabled: form.commerceIntroEnabled,
          landingPcUrl: form.landingPcUrl,
          landingMobileUrl: form.landingMobileUrl,
          moreButton: form.moreButton,
          images: uploadedImages,
        }),
      };
      const ad = form.savedAd?.kakaoAdId
        ? await updateMessageAd(form.savedAd.kakaoAdId, payload)
        : await saveMessageAd(payload);
      form.setSavedAd(ad);
      setStatusMessage({ type: "success", text: "메시지 저장에 성공했습니다." });
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : "저장에 실패했습니다.\n잠시후 다시 시도해주세요.";
      setStatusMessage({ type: "error", text: msg });
    } finally {
      setIsSaving(false);
      setIsLoading(false);
    }
  };

  // ── 테스트 발송 ──
  const handleTestSend = async () => {
    if (!form.savedAd?.kakaoAdId) { alert("먼저 메시지를 저장해주세요."); return; }
    if (testCooldownSeconds > 0) {
      alert(`테스트 발송은 ${testCooldownSeconds}초 후 다시 시도할 수 있습니다.`);
      return;
    }
    const normalizedPhone = normalizePhoneNumber(testPhone);
    if (!normalizedPhone) { alert("테스트 발송 수신 번호를 입력해주세요."); return; }
    if (!isValidMobilePhoneNumber(normalizedPhone)) {
      alert("전화번호 형식이 올바르지 않습니다. 회원가입한 휴대전화 번호를 입력해주세요.");
      return;
    }
    setIsLoading(true);
    try {
      await sendTestMessage(form.savedAd.kakaoAdId, normalizedPhone);
      window.dispatchEvent(new CustomEvent("bizchat:point-refresh"));
      window.sessionStorage.setItem(getCooldownStorageKey(), String(Date.now() + TEST_SEND_COOLDOWN_MS));
      syncCooldownFromStorage();
      setShowTestModal(false);
      setTestPhone("");
      setStatusMessage({ type: "success", text: "테스트 발송이 완료되었습니다." });
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : "테스트 발송 중 오류가 발생했습니다.";
      setStatusMessage({ type: "error", text: msg });
      setShowTestModal(false);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="moment-page tw-flex tw-flex-col tw-min-h-[calc(100vh-6rem)] tw-relative">
      {isSaving && (
        <div className="tw-fixed tw-inset-0 tw-z-[120] tw-bg-black/20 tw-backdrop-blur-[1px] tw-flex tw-items-center tw-justify-center">
          <div className="tw-bg-white tw-rounded-2xl tw-shadow-xl tw-border tw-border-gray-200 tw-px-6 tw-py-5 tw-flex tw-items-center tw-gap-3">
            <div className="tw-h-5 tw-w-5 tw-rounded-full tw-border-2 tw-border-[#5D87FF] tw-border-t-transparent tw-animate-spin" />
            <div className="tw-text-sm tw-font-semibold tw-text-gray-700">이미지 업로드 및 저장 중...</div>
          </div>
        </div>
      )}
      <div className="tw-w-full tw-max-w-[1440px] tw-mx-auto tw-px-6 tw-pb-8">
        <div className="tw-pt-6 tw-flex-shrink-0">
          <MomentHeaderCard
            title="카카오모먼트 광고 메시지 생성"
            description="카카오모먼트 광고 메시지를 작성하고 화면에서 미리보기까지 확인합니다."
          />
        </div>

        {statusMessage && (
          <div
            className={`tw-fixed tw-top-24 tw-right-6 tw-z-[100] tw-w-[320px] tw-max-w-[calc(100vw-2rem)] tw-rounded-xl tw-border tw-px-4 tw-py-3 tw-shadow-lg tw-backdrop-blur-sm tw-transition-all tw-duration-300 ${
              statusMessage.type === "success"
                ? "tw-border-emerald-600 tw-bg-emerald-600 tw-text-white"
                : "tw-border-red-600 tw-bg-red-600 tw-text-white"
            } ${showToast ? "tw-translate-x-0 tw-opacity-100" : "tw-translate-x-6 tw-opacity-0"}`}
          >
            <div className="tw-text-sm tw-font-semibold">
              {statusMessage.type === "success" ? "저장 성공" : "처리 실패"}
            </div>
            <div className="tw-mt-1 tw-text-sm tw-whitespace-pre-line">{statusMessage.text}</div>
          </div>
        )}

        <div className="tw-flex tw-gap-8 tw-pt-6">
          {/* 미리보기 */}
          <div className="tw-w-full lg:tw-w-[400px] tw-flex-shrink-0 tw-order-2 lg:tw-order-1">
            <MomentPhonePreview
              channelLabel={form.channel}
              messageName={form.messageName}
              body={form.messageBody}
              messageTitle={form.messageTitle}
              imagePreview={form.selectedType.supportsImage ? form.wideImage.imagePreview : null}
              typeOption={form.selectedType}
              messageType={form.messageType}
              buttons={form.buttons}
              buttonsEnabled={form.buttonsEnabled}
              ageAuthEnabled={form.ageAuthEnabled}
              adFlag={form.adFlag}
              shareFlag={form.shareFlag}
              couponEnabled={form.couponEnabled}
              coupon={form.coupon}
              moreButtonEnabled={form.moreButtonEnabled}
              listItems={form.listItems}
              carouselItems={form.carouselItems}
              commerceIntroEnabled={form.commerceIntroEnabled}
              commerceIntro={form.commerceIntro}
            />
          </div>

          {/* 입력 폼 */}
          <div className="tw-flex-1 tw-overflow-y-auto tw-bg-gray-50 tw-order-1 lg:tw-order-2 tw-rounded-2xl">
            <div className="tw-bg-white tw-rounded-lg tw-shadow-sm tw-border tw-border-gray-200 tw-p-6 tw-space-y-6">
              {/* 발송 취소 배너 (readonly) */}
              {readonly && (
                <div className="tw-bg-rose-50 tw-border tw-border-rose-300 tw-rounded-xl tw-px-4 tw-py-3">
                  <div className="tw-font-semibold tw-text-rose-700 tw-text-sm">
                    {readonlyDetail?.status === "ADMIN_STOP"
                      ? "관리자에 의해 취소된 광고입니다."
                      : readonlyDetail?.status === "KAKAO_ADMIN_STOP"
                        ? "카카오 관리자에 의해 중지된 광고입니다."
                        : "발송이 취소된 광고입니다."}
                  </div>
                  <div className="tw-text-xs tw-text-rose-600 tw-mt-1">
                    재예약이 필요하다면 새로 생성해주세요. 이 광고는 수정 및 재예약이 불가합니다.
                  </div>
                </div>
              )}
              {/* 정책 안내 */}
              <div className="tw-bg-blue-50/60 tw-border tw-border-blue-100 tw-rounded-xl tw-p-4">
                <div className="tw-flex tw-items-center tw-justify-between tw-gap-3">
                  <div className="tw-font-semibold tw-text-gray-800">카카오모먼트 광고 메시지 발송 정책</div>
                  <a
                    href="https://kakaobusiness.gitbook.io/main/ad/moment/messagead/guide"
                    target="_blank"
                    rel="noreferrer"
                    className="tw-text-sm tw-font-medium tw-text-blue-600 hover:tw-text-blue-700 hover:tw-underline"
                  >
                    집행 가이드 보기
                  </a>
                </div>
                <ul className="tw-mt-3 tw-space-y-1.5 tw-text-xs tw-text-gray-700 tw-list-disc tw-pl-5">
                  {MOMENT_POLICY_ITEMS.map((item, index) => (
                    <li
                      key={item}
                      className={index === MOMENT_POLICY_ITEMS.length - 1 ? "tw-text-rose-600 tw-font-medium" : undefined}
                    >
                      {item}
                    </li>
                  ))}
                </ul>
              </div>

              {/* 메시지 작성 폼 */}
              <div className={readonly ? "tw-pointer-events-none tw-select-none tw-opacity-60" : ""}>
              <MomentMessageComposer
                messageName={form.messageName}
                channel={form.channel}
                channels={[channelLabel]}
                messageType={form.messageType}
                channelLocked={!!form.savedAd}
                messageTypeLocked={form.isLoadedDraft}
                messageTypeNotice={
                  form.isLoadedDraft
                    ? "기존 메시지와 동일한 메시지 유형만 선택할 수 있습니다. 메시지 유형 변경을 원할 경우 새로운 메시지를 생성해주세요."
                    : null
                }
                body={form.messageBody}
                messageTitle={form.messageTitle}
                imageName={form.wideImage.imageName}
                buttons={form.buttons}
                buttonsEnabled={form.buttonsEnabled}
                titleEnabled={form.titleEnabled}
                ageAuthEnabled={form.ageAuthEnabled}
                adFlag={form.adFlag}
                shareFlag={form.shareFlag}
                couponEnabled={form.couponEnabled}
                coupon={form.coupon}
                moreButtonEnabled={form.moreButtonEnabled}
                landingPcUrl={form.landingPcUrl}
                landingMobileUrl={form.landingMobileUrl}
                moreButton={form.moreButton}
                listItems={form.listItems}
                carouselItems={form.carouselItems}
                commerceIntroEnabled={form.commerceIntroEnabled}
                commerceIntro={form.commerceIntro}
                onMessageNameChange={form.setMessageName}
                onChannelChange={form.setChannel}
                onMessageTypeChange={form.handleMessageTypeChange}
                onBodyChange={form.setMessageBody}
                onMessageTitleChange={form.setMessageTitle}
                onImageChange={form.handleImageChange}
                onLibrarySelect={form.handleWideImageLibrarySelect}
                onButtonChange={form.handleButtonChange}
                onAddButton={form.addButton}
                onRemoveButton={form.removeButton}
                onButtonsEnabledChange={form.setButtonsEnabled}
                onTitleEnabledChange={form.setTitleEnabled}
                onAgeAuthChange={form.handleAgeAuthChange}
                onAdFlagChange={form.setAdFlag}
                onShareFlagChange={form.handleShareFlagChange}
                onCouponEnabledChange={form.setCouponEnabled}
                onCouponChange={(key, value) => form.setCoupon((prev) => ({ ...prev, [key]: value }))}
                onMoreButtonEnabledChange={form.setMoreButtonEnabled}
                onLandingChange={(key, value) =>
                  key === "pc" ? form.setLandingPcUrl(value) : form.setLandingMobileUrl(value)
                }
                onMoreButtonChange={(key, value) =>
                  form.setMoreButton((prev) => ({ ...prev, [key]: value }))
                }
                onListItemChange={form.handleListItemChange}
                onListItemImageChange={form.handleListItemImageChange}
                onListItemLibrarySelect={form.handleListItemLibrarySelect}
                onCarouselItemChange={form.handleCarouselItemChange}
                onCarouselItemImageChange={form.handleCarouselItemImageChange}
                onCarouselItemLibrarySelect={form.handleCarouselItemLibrarySelect}
                onCommerceIntroImageChange={form.handleCommerceIntroImageChange}
                onCommerceIntroLibrarySelect={form.handleCommerceIntroLibrarySelect}
                onCommerceIntroEnabledChange={form.handleCommerceIntroEnabledChange}
                onCommerceIntroChange={form.handleCommerceIntroChange}
                onAddCarouselItem={form.addCarouselItem}
                onRemoveCarouselItem={form.removeCarouselItem}
                onCarouselItemShareFlagChange={form.handleCarouselItemShareFlagChange}
                onCarouselItemSecondaryButtonEnabledChange={form.handleCarouselItemSecondaryButtonEnabledChange}
                onCarouselItemCouponEnabledChange={form.handleCarouselItemCouponEnabledChange}
                onCarouselItemCouponChange={form.handleCarouselItemCouponChange}
                onAddListItem={form.addListItem}
                onRemoveListItem={form.removeListItem}
              />
              </div>

              {/* 하단 액션 버튼 */}
              <div className="tw-flex tw-justify-end tw-items-center tw-gap-2 tw-pt-4 tw-border-t tw-border-gray-100">
                {readonly ? (
                  <div className="tw-w-full tw-space-y-4">
                    {readonlyDetail?.status === "ADMIN_STOP" && readonlyDetail.adminCancelReason && (
                      <div className="tw-bg-red-50 tw-border tw-border-red-200 tw-rounded-xl tw-px-4 tw-py-3">
                        <div className="tw-text-sm tw-font-semibold tw-text-red-700">취소 사유</div>
                        <div className="tw-mt-1 tw-text-sm tw-text-red-600 tw-whitespace-pre-line">
                          {readonlyDetail.adminCancelReason}
                        </div>
                      </div>
                    )}
                    <div className="tw-flex tw-justify-end">
                      <button
                        type="button"
                        onClick={() => navigate("/moment/list")}
                        className="tw-px-4 tw-py-2 tw-bg-white hover:tw-bg-gray-50 tw-text-gray-500 tw-border tw-border-gray-200 tw-rounded-lg tw-text-sm tw-font-medium"
                      >
                        목록으로
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                {form.savedAd && (
                  <button
                    type="button"
                    onClick={() => setShowTestModal(true)}
                    disabled={isLoading || testCooldownSeconds > 0}
                    className="tw-px-4 tw-py-2 tw-bg-white hover:tw-bg-gray-50 tw-text-gray-700 tw-border tw-border-gray-200 tw-rounded-lg tw-text-sm tw-font-semibold disabled:tw-opacity-50"
                  >
                    {testCooldownSeconds > 0 ? `테스트 발송 (${testCooldownSeconds})` : "테스트 발송"}
                  </button>
                )}
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={isLoading}
                  className="tw-px-4 tw-py-2 tw-bg-white hover:tw-bg-gray-50 tw-text-gray-700 tw-border tw-border-gray-200 tw-rounded-lg tw-text-sm tw-font-semibold disabled:tw-opacity-50 tw-inline-flex tw-items-center tw-gap-2"
                >
                  {isSaving && <span className="tw-h-4 tw-w-4 tw-rounded-full tw-border-2 tw-border-gray-400 tw-border-t-transparent tw-animate-spin" />}
                  {isSaving ? "저장 중..." : form.savedAd ? "수정 저장" : "저장"}
                </button>
                {form.savedAd && (
                  <button
                    type="button"
                    onClick={() => navigate(`/moment/reserve/${encodeURIComponent(form.savedAd!.kakaoAdId)}`)}
                    disabled={isLoading}
                    className="tw-px-4 tw-py-2 tw-bg-[#5D87FF] hover:tw-bg-[#4570e6] tw-text-white tw-rounded-lg tw-text-sm tw-font-bold tw-border-0 disabled:tw-opacity-50"
                  >
                    발송 예약하기 →
                  </button>
                )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <MomentTestSendModal
        open={showTestModal}
        phone={testPhone}
        isLoading={isLoading}
        cooldownSeconds={testCooldownSeconds}
        onClose={() => { setShowTestModal(false); setTestPhone(""); }}
        onSubmit={handleTestSend}
        saveGuideText="마지막 저장 기준으로 메시지가 발송됩니다. 테스트 발송 전 메시지를 저장해주세요."
      />

    </div>
  );
}
